import { useState, useEffect } from 'react';

export interface HideUntil {
  (until: number): void;
}

/**
 * Saves if something should be hidden, in storage.
 * const [isHidden, hideUntil] = useHideable(`expiryBox`);
 * hideUntil(timestamp) where timestamp is in seconds.
 *
 * @param {string} id The key used in localStorage
 * @param {boolean} initial Initial value
 * @param {Storage} storage The storage chosen (local, session)
 */
export function useHideable({
  id,
  initial = true,
  externalStorage,
}: {
  id: string;
  externalStorage?: Storage;
  initial: boolean;
}): [boolean, HideUntil] {
  const [hidden, setHidden] = useState(initial);

  useEffect(() => {
    try {
      const storage = externalStorage ?? localStorage;

      const timestamp = parseInt(storage.getItem(id) || '', 10);
      const now = Math.round(new Date().getTime() / 1000);

      if (timestamp === -1) {
        setHidden(true);
      } else if (timestamp && timestamp > now) {
        setHidden(true);
      } else {
        setHidden(false);
        if (timestamp) storage.removeItem(id);
      }
    } catch {
      // ..
    }
  }, [id, externalStorage]);

  const hideUntil: HideUntil = (until: number, hideInstantLy = true) => {
    try {
      const storage = externalStorage ?? localStorage;
      setHidden(hideInstantLy); // Hide instantly
      if (storage) {
        storage.setItem(id, until.toString());
      }
    } catch {
      // ..
    }
  };

  return [hidden, hideUntil];
}
